import React, { createContext, useContext, useState, useEffect } from 'react';
import { generateId } from '../utils/helpers';
import { useCRM } from './CRMContext';
import { mockAI } from '../services/mockAI';
import { usePlaybook } from './PlaybookContext';
// NEW: Import sequence context to allow simplified enrollment simulation
// In real app, this might be decoupled via database listeners or a unified automation engine
import { useSequence } from './SequenceContext';

const LeadIntelContext = createContext();

export const useLeadIntel = () => useContext(LeadIntelContext);

export const LeadIntelProvider = ({ children }) => {
  const { companies, contacts, deals, addActivity, addDeal } = useCRM();
  const { runAiNextBestAction } = usePlaybook();
  const { enrollContact } = useSequence();

  // --- STATE ---
  const [intentSources, setIntentSources] = useState([]);
  const [signals, setSignals] = useState([]);
  const [scores, setScores] = useState([]);
  const [scoringRules, setScoringRules] = useState([]);
  const [triggerRules, setTriggerRules] = useState([]);
  const [syncJobs, setSyncJobs] = useState([]);
  const [cooldowns, setCooldowns] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [alerts, setAlerts] = useState([]);

  // --- ENGINE LOGIC: MATCHING ---
  const matchSignalToEntity = (signal) => {
    // 1. Exact Match on OrgNr
    if (signal.company_orgno) {
      const company = companies.find(c => c.org_number === signal.company_orgno);
      if (company) return { type: 'company', id: company.company_id, name: company.name };
    }
    // 2. Fuzzy Match on Name
    if (signal.company_name) {
      const cleanName = (name) => name.toLowerCase().replace(/ as| asa| ab| ltd/g, '').trim();
      const signalName = cleanName(signal.company_name);
      const company = companies.find(c => cleanName(c.name) === signalName);
      if (company) return { type: 'company', id: company.company_id, name: company.name };
    }
    return null; // No match found
  };

  // --- ENGINE LOGIC: SCORING ---
  const calculateScore = (entityId) => {
    const entitySignals = signals.filter(s => s.company_id === entityId);
    let totalScore = 0;
    const reasons = [];

    entitySignals.forEach(sig => {
      const rules = scoringRules.filter(r => 
        (r.scope === 'Global' || r.company_id === entityId || r.project_id) &&
        (r.when_signal_type === '*' || r.when_signal_type === sig.signal_type) &&
        (r.when_priority_hint === '*' || r.when_priority_hint === sig.priority_hint)
      );
      
      rules.forEach(r => {
        totalScore += r.score_delta;
        reasons.push(`${r.name} (${sig.occurred_at.split('T')[0]})`);
      });
    });

    let band = 'Cold';
    if (totalScore > 30) band = 'Warm';
    if (totalScore > 60) band = 'Hot';

    return { score: totalScore, band, reasons: [...new Set(reasons)] };
  };

  // --- ENGINE LOGIC: TRIGGERS ---
  const checkCooldown = (ruleId, entityId, cooldownDays) => {
    const existing = cooldowns.find(c => c.rule_id === ruleId && c.target_entity_id === entityId);
    if (!existing) return false;
    
    const diffDays = (new Date() - new Date(existing.last_fired_at)) / (1000 * 60 * 60 * 24);
    return diffDays < cooldownDays;
  };

  const executeTriggers = async (entityId, scoreData, newSignal = null) => {
    const activeTriggers = triggerRules.filter(t => t.enabled);

    for (const rule of activeTriggers) {
      const scoreMatch = rule.when_score_band === 'Any' || rule.when_score_band === scoreData.band;
      const signalMatch = !newSignal || rule.when_signal_type === '*' || rule.when_signal_type === newSignal.signal_type;

      if (scoreMatch && signalMatch) {
        if (checkCooldown(rule.tr_id, entityId, rule.cooldown_days)) {
          console.log(`Trigger ${rule.name} skipped due to cooldown.`);
          continue;
        }

        logAudit('TriggerFire', rule.tr_id, { entityId, action: rule.action_type });

        switch (rule.action_type) {
          case 'CreateTask':
            addActivity({
              type: 'Oppgave',
              title: rule.task_title.replace('{{company_name}}', companies.find(c => c.company_id === entityId)?.name || 'Ukjent'),
              description: rule.task_notes || `Auto-generert av Lead Intel. Signal: ${newSignal?.title}`,
              company_id: entityId,
              status: 'Planlagt',
              date: new Date().toISOString()
            });
            break;
          case 'AI_CoachEvent':
            const deal = deals.find(d => d.company_id === entityId);
            if (deal && deal.project_id) {
              runAiNextBestAction(deal.project_id);
            }
            break;
          case 'StartSequence':
            if (rule.sequence_id) {
              // Find a contact for this company to enroll
              const contact = contacts.find(c => c.company_id === entityId);
              if (contact) {
                try {
                  enrollContact(contact.contact_id, rule.sequence_id);
                  addActivity({
                    type: 'Oppgave',
                    title: 'Verifiser sekvens-start',
                    description: `Automatisering forsøkte å starte sekvens for ${contact.name}. Sjekk E-post Sekvenser for å godkjenne første utkast.`,
                    company_id: entityId,
                    status: 'Planlagt',
                    date: new Date().toISOString()
                  });
                } catch (e) {
                  console.error("Failed to enroll:", e);
                }
              } else {
                console.warn("No contact found for company, cannot start sequence.");
                // Fallback task
                addActivity({
                  type: 'Oppgave',
                  title: 'Mangler kontaktperson for sekvens',
                  description: `Trigger forsøkte å starte sekvens, men fant ingen kontaktperson. Legg til kontakt for ${companies.find(c => c.company_id === entityId)?.name}.`,
                  company_id: entityId,
                  status: 'Planlagt',
                  date: new Date().toISOString()
                });
              }
            }
            break;
        }

        setCooldowns(prev => [...prev, { rule_id: rule.tr_id, target_entity_id: entityId, last_fired_at: new Date().toISOString() }]);
      }
    }
  };

  // --- ACTIONS ---
  const handleAlert = (message, type = 'info') => {
    const id = generateId('alert');
    setAlerts(prev => [...prev, { id, message, type, timestamp: new Date() }]);
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.id !== id));
    }, 5000);
  };

  const logAudit = (actionType, targetId, details) => {
    const entry = {
      audit_id: generateId('audit'),
      occurred_at: new Date().toISOString(),
      action_type: actionType,
      target_id: targetId,
      details
    };
    setAuditLog(prev => [entry, ...prev]);
  };

  const ingestSignal = async (rawSignal) => {
    const match = matchSignalToEntity(rawSignal);
    const linkedSignal = {
      ...rawSignal,
      signal_id: generateId('sig'),
      ingested_at: new Date().toISOString(),
      company_id: match ? match.id : null,
      company_name: match ? match.name : rawSignal.company_name
    };

    setSignals(prev => [linkedSignal, ...prev]);
    logAudit('SignalIngest', linkedSignal.signal_id, { source: linkedSignal.source_id, match: !!match });

    if (match) {
      const scoreData = calculateScore(match.id);
      setScores(prev => {
        const others = prev.filter(s => s.company_id !== match.id);
        return [...others, { company_id: match.id, ...scoreData, last_updated_at: new Date().toISOString() }];
      });
      await executeTriggers(match.id, scoreData, linkedSignal);
    }
  };

  const analyzeSignal = async (signal) => {
    return await mockAI.analyzeSignal(signal);
  };

  const addScoringRules = (rules) => {
    const newRules = rules.map(r => ({ ...r, rule_id: generateId('sr') }));
    setScoringRules(prev => [...prev, ...newRules]);
  };

  const addTriggerRules = (rules) => {
    const newRules = rules.map(r => ({ ...r, tr_id: generateId('tr') }));
    setTriggerRules(prev => [...prev, ...newRules]);
  };

  const clearProjectRules = (projectId) => {
    setScoringRules(prev => prev.filter(r => r.project_id !== projectId));
    setTriggerRules(prev => prev.filter(r => r.project_id !== projectId));
  };

  useEffect(() => {
    if (intentSources.length === 0) {
      setIntentSources([
        { source_id: 'src-1', name: 'Brønnøysund Monitor', provider_type: 'BRREG', status: 'På', update_mode: 'Daglig' },
        { source_id: 'src-2', name: 'Tech News Feed', provider_type: 'NewsFeed', status: 'På', update_mode: 'Sanntid' }
      ]);

      setScoringRules([
        { rule_id: 'sr-1', name: 'Funding Signal', when_signal_type: 'Funding', score_delta: 25, scope: 'Global' },
        { rule_id: 'sr-2', name: 'Ny Leder', when_signal_type: 'LeadershipChange', score_delta: 15, scope: 'Global' }
      ]);

      setTriggerRules([
        { 
          tr_id: 'tr-1', 
          name: 'Hot Lead Task', 
          enabled: true, 
          when_score_band: 'Hot', 
          action_type: 'CreateTask', 
          task_title: '🔥 Hot Lead: {{company_name}}', 
          cooldown_days: 7,
          scope: 'Global'
        },
        // NEW Trigger Rule for Compliance Microsite Click
        {
          tr_id: 'tr-compliance-click',
          name: "Compliance Microsite klikk → Hot lead",
          enabled: true,
          scope: "Project",
          when_signal_type: "ComplianceInterest",
          when_score_band: "Any",
          action_type: "CreateTask",
          task_title: "Compliance-interesse: følg opp innen 24t",
          task_notes: "Lead klikket på Compliance Oversikt. Bruk AI Coach for personlig oppfølging.",
          cooldown_days: 5
        }
      ]);
    }
  }, []);

  const runSync = async (sourceId) => {
    const jobId = generateId('job');
    setSyncJobs(prev => [{ job_id: jobId, source_id: sourceId, status: 'Running', started_at: new Date().toISOString() }, ...prev]);
    
    setTimeout(() => {
      // Mock Ingesting a signal - SIMULATING A REGULATORY NEED THIS TIME
      const mockSignal = {
        source_id: sourceId,
        signal_type: 'RegulatoryNeed', // Changed to trigger the specific rule
        company_name: 'Telenor Bedrift',
        title: 'Nytt lovkrav: Åpenhetsloven',
        summary: 'Selskapet må rapportere på aktsomhetsvurderinger innen 1. juli.',
        occurred_at: new Date().toISOString(),
        evidence_url: 'https://exmaple.com/lovdata',
        priority_hint: 'Høy'
      };
      
      ingestSignal(mockSignal);
      setSyncJobs(prev => prev.map(j => j.job_id === jobId ? { ...j, status: 'OK', finished_at: new Date().toISOString() } : j));
    }, 1500);
  };

  return (
    <LeadIntelContext.Provider value={{
      intentSources,
      signals,
      scores,
      triggerRules,
      scoringRules,
      syncJobs,
      auditLog,
      alerts,
      runSync,
      ingestSignal,
      analyzeSignal,
      toggleTrigger: (id) => setTriggerRules(prev => prev.map(r => r.tr_id === id ? { ...r, enabled: !r.enabled } : r)),
      addScoringRules,
      addTriggerRules,
      clearProjectRules
    }}>
      {children}
    </LeadIntelContext.Provider>
  );
};